'use client';

import { useState, useEffect } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth } from '@/lib/firebase/config';
import { setUserContext } from '@/lib/sentry';
import { identifyUser, clearUserIdentity } from '@/lib/analytics';
import type { User } from '@/types';
import { getCurrentUserDocument } from '@/lib/firebase/auth';

export function useAuth() {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userDoc, setUserDoc] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      // Set user context for error tracking and analytics
      if (firebaseUser) {
        setUserContext({ id: firebaseUser.uid });
        try {
          const doc = await getCurrentUserDocument();
          setUserDoc(doc);
          // Identify user in analytics
          if (doc) {
            identifyUser(firebaseUser.uid, {
              plan: doc.plan,
            });
          }
        } catch (error) {
          console.error('Error fetching user document:', error);
        }
      } else {
        setUserContext(null);
        clearUserIdentity();
        setUserDoc(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return {
    user,
    userDoc,
    loading,
    isAuthenticated: !!user,
  };
}
